import React from 'react'
import { LinkButton } from '@enamelai/design-system'
import { Icon as ArrowLeftIcon } from '@enamelai/design-system/icons/Arrow_2_left'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import { useReportInPlaceExpandOptional } from '@components/pages/Reports/shared/ReportInPlaceExpandContext'
import { toReportSectionTitle } from '@utils/textCase.util'

export function useReportFullViewSection(sectionId: string): {
  isFullView: boolean
  enterFullView: () => void
  exitFullView: () => void
} {
  const expandCtx = useReportInPlaceExpandOptional()
  const isFullView = expandCtx?.expandedSectionId === sectionId

  return {
    isFullView,
    enterFullView: () => {
      expandCtx?.expand(sectionId)
    },
    exitFullView: () => {
      expandCtx?.exit()
    },
  }
}

type ReportFullViewTableTitleProps = {
  sectionId: string
  title: React.ReactNode
  enterAriaLabel?: string
  exitAriaLabel?: string
  disableEnter?: boolean
}

function ReportFullViewTableTitle({
  sectionId,
  title,
  enterAriaLabel,
  exitAriaLabel,
  disableEnter = false,
}: ReportFullViewTableTitleProps): React.ReactElement {
  const { isFullView, enterFullView, exitFullView } =
    useReportFullViewSection(sectionId)
  const expandCtx = useReportInPlaceExpandOptional()
  const displayTitle =
    typeof title === 'string' ? toReportSectionTitle(title) : title
  const titleText = typeof displayTitle === 'string' ? displayTitle : 'table'

  if (isFullView) {
    return (
      <LinkButton
        type="button"
        underline={false}
        leftIcon={<ArrowLeftIcon aria-hidden />}
        className="reports-patient-base-health__risk-patients-title reports-patient-base-health__risk-patients-title--back"
        onClick={exitFullView}
        aria-label={exitAriaLabel ?? `Exit full view of ${titleText}`}
      >
        <h3>{displayTitle}</h3>
      </LinkButton>
    )
  }

  if (disableEnter) {
    return <h3>{displayTitle}</h3>
  }

  return (
    <LinkButton
      type="button"
      underline={false}
      rightIcon={<ArrowRightIcon aria-hidden />}
      className="reports-patient-base-health__risk-patients-title"
      onClick={enterFullView}
      disabled={expandCtx == null}
      aria-label={enterAriaLabel ?? `Open full view of ${titleText}`}
    >
      <h3>{displayTitle}</h3>
    </LinkButton>
  )
}

export default ReportFullViewTableTitle
